/* PDP image gallery — crossfades all stills inside one fixed 4:5 stage.

   Behaviour (Rahul, 2026-08-26):
     · the 3s auto-advance is ALWAYS on and wraps from the last slide back to the first
     · PRESS AND HOLD anywhere on the image freezes it; releasing restarts a full 3s
     · SWIPE / mouse-drag left or right steps to the next or previous slide
     · dots jump straight to a slide
     · desktop also gets < > arrows over the stage, and Left/Right on the keyboard
     · HOVER PAUSES on a real mouse (Rahul, 2026-09-07) — see the note by the handlers

   Lives in its own file rather than in scroll.js so the gallery can be changed without
   re-uploading 12KB of unrelated scroll code through the theme API.

   The element is [data-pdp-gallery], NOT [data-gallery]: an older cycler still sitting in
   the deployed scroll.js keys off [data-gallery], and the rename is what stops the two of
   them fighting over the same slides. Do not rename it back.

   🔑 WHY THE POINTER HANDLERS LIVE ON .pdp-stage AND NOT ON THE WHOLE GALLERY
   The previous version bound pointerdown to the gallery root and called setPointerCapture
   on it. Pointer capture RETARGETS the following pointerup and click to the capturing
   element, so a click on a dot was delivered to .pdp-g instead of to the button — the dot
   listeners never fired and the dots did nothing, on desktop and on touch. Capture is
   still what makes a drag that leaves the image complete cleanly, so it stays, but it is
   now scoped to the image stage only, with the dots and arrows outside it. The
   closest('.g-dot,.g-arrow') guard is a second belt for anything that ends up inside. */
(function () {
  'use strict';
  var g = document.querySelector('[data-pdp-gallery]');
  if (!g) return;

  var stage = g.querySelector('.pdp-stage') || g;
  var slides = [].slice.call(g.querySelectorAll('.g-slide'));
  var dotWrap = g.querySelector('.g-dots');
  var dots = [].slice.call(g.querySelectorAll('.g-dot'));
  if (slides.length < 2) return;

  /* Control styles are injected here, not in style.css, because that file is 48KB — past
     what the theme API takes inline — so it has no push route right now. Move them into
     style.css on its next real deploy and delete this block. */
  var CSS = [
    /* ---- dots ----
       🔑 THE DOTS USED TO BE UNHITTABLE. Each small dot carried an oversized square pseudo
       as its tap target — 44px in style.css, 40px in this file's old block — but the dots
       sat on a 13px pitch, so those targets overlapped three deep and the LAST one painted
       always won the hit test. Aiming at dot 0 selected dot 1; aiming at dot 2 selected
       dot 4. Measured on the live page, every dot but the final one resolved to a
       different dot, so the row could not be used at all.

       The target is now the button itself — 22px wide, 34px tall, sitting side by side
       with no gap so nothing can overlap — and the visible 6px circle is drawn as ::before
       inside it. Pitch goes 13px -> 22px, so the row reads a touch airier than before;
       that is the cost of every tap landing where it was aimed.

       Hidden below three slides: with a pair there is nothing worth steering. */
    '.g-dots{display:flex;gap:0;justify-content:center;margin-top:0}',
    '.pdp-g[data-slides="2"] .g-dots{display:none}',
    '.g-dot{width:22px;height:34px;padding:0;border:0;background:none;cursor:pointer;',
      'display:grid;place-items:center;-webkit-appearance:none;appearance:none}',
    /* The DEPLOYED style.css still carries an older generation of dot rules that the local
       source no longer has — 7px dots, an 8px gap, `.g-dot.is-on{transform:scale(1.25)}`
       and a 44px `.g-dot:after`. They cannot be edited out (style.css has no push route),
       and `.g-dot.is-on` outranks the plain `.g-dot` above, so both are cancelled here by
       name. Deleting either of these two lines brings the overlapping targets back. */
    '.g-dot::after{content:none;display:none}',
    '.g-dot.is-on{background:none;transform:none;border-radius:0}',
    '.g-dot::before{content:"";display:block;width:6px;height:6px;border-radius:50%;',
      'background:var(--line);transition:background .3s ease,transform .3s ease}',
    '.g-dot.is-on::before{background:var(--deep);transform:scale(1.3)}',
    '.g-dot:focus-visible{outline:1px solid var(--ink);outline-offset:-6px;border-radius:4px}',

    /* Horizontal drags must reach our handler, so only vertical panning is left to the
       browser. grab/grabbing tells a desktop viewer the image is draggable at all. */
    '.pdp-stage{touch-action:pan-y;cursor:grab;user-select:none;-webkit-user-select:none}',
    '.pdp-stage.is-held{cursor:grabbing}',
    '.g-slide{-webkit-user-drag:none;user-drag:none;pointer-events:none}',

    /* ---- keep the whole gallery inside a short laptop window ----
       .pdp-g is sticky at top:88px and its height is width*1.25 + 34px of dots. On a
       1280x720 window that came to 788px against a 720px viewport, so while the column was
       stuck the dots — and the bottom 68px of the photograph — sat permanently below the
       fold. Measured across the scroll: the dots were off-screen for the entire first
       800px, which is the whole time anyone is actually looking at the product.

       Capping the COLUMN width rather than the stage height keeps the 4:5 box exact:
       height = width * 1.25, so width = available * 0.8. Inert on any window tall enough
       for the natural size, which is most real laptops. */
    '@media(min-width:781px){.pdp-g{max-width:calc((100vh - 140px) * .8)}}',

    /* ---- desktop arrows ----
       Hidden by default and revealed only where there is a real hovering pointer and room
       for them. On touch the gesture is the control, and a permanent chevron over the
       piece is just clutter. */
    '.g-arrow{display:none}',
    '@media(min-width:781px) and (hover:hover){',
      '.g-arrow{display:grid;place-items:center;position:absolute;top:50%;z-index:3;',
        'width:38px;height:38px;margin-top:-19px;padding:0;border:0;border-radius:50%;',
        'cursor:pointer;background:rgba(244,237,226,.82);color:var(--ink);',
        '-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);',
        'opacity:0;transform:scale(.94);',
        'transition:opacity .25s ease,transform .25s ease,background .25s ease}',
      '.g-arrow.prev{left:14px}',
      '.g-arrow.next{right:14px}',
      /* Shown while the pointer is anywhere in the gallery, and whenever an arrow itself
         has keyboard focus so a tabbing visitor can see what they are on. */
      '.pdp-g:hover .g-arrow,.g-arrow:focus-visible{opacity:1;transform:none}',
      '.g-arrow:hover{background:rgba(244,237,226,.96)}',
      '.g-arrow:focus-visible{outline:2px solid var(--ink);outline-offset:2px}',
      '.g-arrow svg{width:15px;height:15px;display:block}',
    '}',
    '@media(prefers-reduced-motion:reduce){.g-arrow{transition:none;transform:none}}'
  ].join('');
  (function () {
    var st = document.createElement('style');
    st.textContent = CSS;
    document.head.appendChild(st);
  })();

  g.setAttribute('data-slides', slides.length);

  var every = parseInt(g.getAttribute('data-interval'), 10) || 3000;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var i = 0, timer = null, held = false, hovering = false;

  function show(n) {
    i = (n + slides.length) % slides.length;          /* wraps both ways — it is a loop */
    slides.forEach(function (s, k) { s.classList.toggle('is-on', k === i); });
    dots.forEach(function (d, k) {
      d.classList.toggle('is-on', k === i);
      d.setAttribute('aria-selected', k === i ? 'true' : 'false');
    });
  }

  /* Always restart from zero rather than resuming a part-spent interval, so a slide the
     viewer just chose gets its full 3 seconds instead of a leftover sliver. */
  function start() {
    stop();
    if (held || hovering || reduced || document.hidden) return;
    timer = setInterval(function () { show(i + 1); }, every);
  }
  function stop() { clearInterval(timer); timer = null; }

  function go(n) { show(n); start(); }

  /* ---- dots ----
     Delegated on the wrapper as well as bound per button: the wrapper listener is what
     keeps working if the dots are ever re-rendered, and neither can be retargeted away
     now that capture is confined to the stage. */
  if (dotWrap) {
    dotWrap.addEventListener('click', function (e) {
      var d = e.target.closest ? e.target.closest('.g-dot') : null;
      if (!d) return;
      e.preventDefault();
      go(parseInt(d.getAttribute('data-go'), 10) || 0);
    });
  }

  /* ---- desktop arrows ----
     Built here rather than in product.liquid so the whole control surface ships in one
     asset; the template already has no markup for them. */
  function arrow(cls, label, d) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'g-arrow ' + cls;
    b.setAttribute('aria-label', label);
    b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" ' +
      'aria-hidden="true"><polyline points="' + d + '"/></svg>';
    b.addEventListener('click', function (e) {
      e.preventDefault();
      go(i + (cls === 'next' ? 1 : -1));
    });
    stage.appendChild(b);
  }
  arrow('prev', 'Previous image', '15 5 8 12 15 19');
  arrow('next', 'Next image', '9 5 16 12 9 19');

  /* ---- hover pauses, leaving resumes ----
     An earlier version deliberately had NO hover-pause, on the grounds that a cursor
     resting over the image on its way to Add to bag would stall the gallery and read as
     broken. Rahul asked for it anyway on 2026-09-07, and the objection is answered by
     making the resume immediate and total: mouseleave calls start(), which lays down a
     FULL fresh interval, so the moment the cursor is off the picture the cycle carries on
     exactly as if it had never stopped.

     Bound only where there is a real hovering pointer. On a touchscreen a tap synthesises
     mouseenter with no matching mouseleave, which would freeze the gallery for the rest of
     the visit — the one failure mode worth guarding against. hovering is a separate flag
     from held so that releasing a drag while the cursor is still on the image keeps it
     paused, which is what the viewer is asking for by leaving it there. */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    stage.addEventListener('mouseenter', function () { hovering = true; stop(); });
    stage.addEventListener('mouseleave', function () { hovering = false; start(); });
  }

  /* ---- press-and-hold to freeze, drag or swipe to move ----
     One pointer handler covers touch, mouse and pen, so desktop gets the same hold and
     drag as a phone. Capture means a drag that leaves the image still completes.

     A gesture is a SWIPE if it travels more than SWIPE_PX horizontally; anything else was
     a hold, and a hold's only job is to have paused the timer. A plain tap or click is
     just a very short hold — it pauses, then release gives the slide a fresh 3s, which is
     what a viewer who tapped to look at something actually wants. */
  var SWIPE_PX = 40;
  var startX = 0, startY = 0, tracking = false, capturedId = null;

  stage.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest && e.target.closest('.g-arrow,.g-dot')) return;
    tracking = true;
    held = true;
    startX = e.clientX; startY = e.clientY;
    stage.classList.add('is-held');
    stop();                                    /* freeze immediately on touch */
    try { stage.setPointerCapture(e.pointerId); capturedId = e.pointerId; } catch (err) {}
  });

  function release(e) {
    if (!tracking) return;
    tracking = false;
    held = false;
    stage.classList.remove('is-held');
    if (capturedId !== null) {
      try { stage.releasePointerCapture(capturedId); } catch (err) {}
      capturedId = null;
    }
    var dx = e.clientX - startX;
    var dy = e.clientY - startY;
    /* Only treat it as a swipe if it is mostly horizontal — otherwise a vertical page
       scroll that happens to start on the image would flick the slide sideways. */
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      show(dx < 0 ? i + 1 : i - 1);            /* left = next, right = previous */
    }
    start();                                   /* full 3s from here, either way */
  }

  stage.addEventListener('pointerup', release);
  stage.addEventListener('pointercancel', function () {
    tracking = false; held = false; capturedId = null;
    stage.classList.remove('is-held');
    start();
  });
  /* A mouse released outside the window never sends pointerup. Without this the timer
     would stay frozen for the rest of the visit. */
  window.addEventListener('blur', function () {
    if (!tracking) return;
    tracking = false; held = false; capturedId = null;
    stage.classList.remove('is-held');
    start();
  });

  /* Don't animate in a background tab, and don't let intervals pile up on return. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop(); else start();
  });

  /* Keyboard, since the dots and arrows are real buttons and can be tabbed to. */
  g.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
  });

  show(0);
  start();
})();
